// import: components
import { Box } from '@mui/material';

// import: assets
import { ReactComponent as CopyDetail } from 'core/assets/svg/modal-icons/copy-detail.svg';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';
import { aspectRatioConfig } from 'core/config/AspectRatioConfig';

// import: constants
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';
import ImageInfoBottomBar from 'apps/studio/studio-tab/components/image-info/components/ImageInfoBottomBar';
import { imageInfoStyles } from 'apps/studio/studio-tab/components/image-info/ImageInfoStyles';
import ShowMoreComponent from 'apps/studio/studio-tab/components/ShowMoreComponent';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';

// import: styles
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import React from 'react';

// import: utils
import { copyToClipboard } from 'core/utils/utils';
import { useSelector } from 'react-redux';
import { stylesSelector } from 'core/store/middleware/StudioCanvasMiddleware';
import { TEXT_TO_IMAGE } from 'core/navigation/Routes';

const styles = imageInfoStyles;
export default function StyleInfo({
	selectedPrompt,
	handleClose,
	feedPromptsModalType,
	hidePrompt,
	setHidePrompt,
	hideNegativePrompt,
	setHideNegativePrompt,
}) {
	const sdStyles = useSelector((state) => stylesSelector(state, TEXT_TO_IMAGE));
	const isStylePresent = Object.values(sdStyles).some(
		(style) => style.id === selectedPrompt.styleId,
	);
	return (
		<Box id="details-cont" sx={styles.promptDetailsCont}>
			<Box
				id="details-scroll-wrapper"
				sx={[
					styles.promptDetailsScrollWrapper,
					reusableStyles.verticalScrollBarStyles,
					{
						'&::-webkit-scrollbar-track': { backgroundColor: 'colors.dark6' },
						'&::-webkit-scrollbar-thumb': { border: `4px solid ${colorPalette.dark6}` },
					},
				]}
			>
				<Box id="prompt-details" sx={styles.detailScreenPromptDetails}>
					<Box id={ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR} />
					<ActionsMenu
						image={selectedPrompt?.media}
						orientation={LayoutOrientation.HORIZONTAL}
						handleClose={handleClose}
						imageId={selectedPrompt?.generationId}
					/>
					<Box sx={[styles.detailCard]}>
						<Box id="model-used" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Style used
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[reusableStyles.flexRow, { gap: '4px' }]}>
										<Box sx={styles.detailCardModelImage}>
											<BaseImage src={selectedPrompt.styleImage} alt={selectedPrompt.styleLabel} />
										</Box>
										<BaseTypography variant="b2_caption" styles={{ color: 'colors.light2' }}>
											{selectedPrompt.styleLabel}
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box sx={[styles.detailCard]}>
						<Box id="prompt" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Prompt
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box
										sx={styles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.prompt);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
							<Box sx={[styles.detailValue]}>
								<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
									{selectedPrompt && (
										<ShowMoreComponent
											hide={hidePrompt}
											value={selectedPrompt.prompt ?? ''}
											showMoreFunc={() => setHidePrompt(false)}
										/>
									)}
								</BaseTypography>
							</Box>
						</Box>
					</Box>
					<Box sx={[styles.detailCard]}>
						<Box id="negative-prompt" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Negative Prompt
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box
										sx={styles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.negativePrompt);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
							<Box sx={[styles.detailValue]}>
								<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
									{selectedPrompt && (
										<ShowMoreComponent
											hide={hideNegativePrompt}
											value={selectedPrompt.negativePrompt ?? ''}
											showMoreFunc={() => setHideNegativePrompt(false)}
										/>
									)}
								</BaseTypography>
							</Box>
						</Box>
						<Box id="prompt-details-divider" sx={styles.divider} />
						<Box id="seed" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Seed
								</BaseTypography>
								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[styles.detailValue]}>
										<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
											{selectedPrompt.seed ?? ''}
										</BaseTypography>
									</Box>
									<Box
										sx={styles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.seed);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
						</Box>
						<Box id="prompt-details-divider" sx={styles.divider} />
						<Box id="aspect-ratio" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Aspect Ratio
								</BaseTypography>
								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[styles.detailValue]}>
										{aspectRatioConfig[selectedPrompt.aspectRatioType]?.icon ?? ''}
										<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
											{aspectRatioConfig[selectedPrompt.aspectRatioType]?.label ?? ''}
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>

			<ImageInfoBottomBar
				selectedPrompt={selectedPrompt}
				handleClose={handleClose}
				feedPromptsModalType={feedPromptsModalType}
				showPublishPrompt={false}
				showReusePrompt={isStylePresent}
			/>
		</Box>
	);
}
